import React, { useEffect, useState, useCallback } from 'react';
import api from '../config/api';

interface Shift {
  id: number;
  clock_in: string;
  clock_out: string;
  duration_minutes: number;
  alerts_sent: number;
  activities_logged: number;
  handoff_notes: string | null;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDurationMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const PAGE_SIZE = 20;

export default function ShiftHistoryPage() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchShifts = useCallback(async (offset: number, append: boolean) => {
    try {
      const res = await api.get(`/monitors/shifts?limit=${PAGE_SIZE}&offset=${offset}`);
      if (append) {
        setShifts((prev) => [...prev, ...res.data.shifts]);
      } else {
        setShifts(res.data.shifts);
      }
      setTotal(res.data.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load shift history');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchShifts(0, false);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchShifts]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchShifts(shifts.length, true);
    setLoadingMore(false);
  };

  const hasMore = shifts.length < total;

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <h2 style={styles.sectionTitle}>Shift History</h2>

        {loading && <p style={styles.statusText}>Loading shifts...</p>}

        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && shifts.length === 0 && (
          <p style={styles.statusText}>No completed shifts yet.</p>
        )}

        {!loading && shifts.length > 0 && (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Clock In</th>
                    <th style={styles.th}>Clock Out</th>
                    <th style={styles.th}>Duration</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Alerts</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Activities</th>
                    <th style={styles.th}>Handoff Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift) => {
                    const isExpanded = expandedId === shift.id;
                    const notes = shift.handoff_notes || '—';
                    const truncated = notes.length > 60 && !isExpanded;
                    return (
                      <tr key={shift.id} style={styles.tr}>
                        <td style={styles.td}>{formatDate(shift.clock_in)}</td>
                        <td style={styles.td}>{formatTime(shift.clock_in)}</td>
                        <td style={styles.td}>{formatTime(shift.clock_out)}</td>
                        <td style={styles.td}>{formatDurationMinutes(shift.duration_minutes)}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{shift.alerts_sent}</td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>{shift.activities_logged}</td>
                        <td style={styles.td}>
                          <span>
                            {truncated ? notes.slice(0, 60) + '...' : notes}
                          </span>
                          {notes.length > 60 && (
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : shift.id)}
                              style={styles.expandBtn}
                            >
                              {isExpanded ? 'less' : 'more'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div style={styles.loadMoreWrap}>
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={styles.loadMoreBtn}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 960,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  sectionTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    marginBottom: 'var(--space-5)',
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 14px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    background: 'var(--table-header-bg)',
    borderBottom: '1px solid var(--border-default)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: {
    padding: '12px 14px',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono)',
    verticalAlign: 'top' as const,
    whiteSpace: 'nowrap' as const,
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    padding: '0 4px',
    fontWeight: 500,
  },
  loadMoreWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 'var(--space-5)',
  },
  loadMoreBtn: {
    padding: '8px 28px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-surface-raised)',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
};
